import React, { useState } from 'react';
import { Modal } from 'antd';

const safeUrl = (value) => {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol)
      ? url.href : null;
  } catch {
    return null;
  }
};

const validBounds = (bounds) => bounds &&
  ['x', 'y', 'width', 'height'].every(key => Number.isFinite(bounds[key])) &&
  bounds.width > 0 && bounds.height > 0;

const FrontierChart = ({ src, hotspots, alt, style }) => {
  const [active, setActive] = useState(null);

  const points = [
    ...['dog', 'riskFree']
      .filter(key => hotspots?.[key])
      .map(key => ({ key, ...hotspots[key] })),
    ...(hotspots?.assets || []).map((point, index) => ({
      key: `asset-${index}`,
      ...point,
    })),
  ].filter(point =>
    Number.isFinite(point.x) &&
    Number.isFinite(point.y) &&
    point.x >= 0 && point.x <= 1 &&
    point.y >= 0 && point.y <= 1
  );

  // Later paths win at intersections; point buttons remain above both lines.
  const lines = ['capitalMarketLine', 'efficientFrontier']
    .filter(key => {
      const line = hotspots?.[key];
      return validBounds(line?.bounds) && Array.isArray(line?.points) &&
        line.points.length >= 2 && line.points.every(point =>
          Number.isFinite(point?.x) && Number.isFinite(point?.y)
        );
    })
    .map(key => ({ key, ...hotspots[key] }));

  const link = safeUrl(active?.url);
  const legends = (
    Array.isArray(hotspots?.legends)
      ? hotspots.legends
      : hotspots?.sealLegend ? [{ key: 'seal', ...hotspots.sealLegend }] : []
  ).filter(legend =>
    validBounds(legend?.bounds) &&
    typeof legend.details === 'string' && legend.details.trim().length > 0
  );

  return (
    <>
      <div style={{ position: 'relative', width: '100%', ...style }}>
        <img
          src={src}
          alt={alt}
          style={{ display: 'block', width: '100%', height: 'auto' }}
        />
        {lines.map(line => {
          const { x, y, width, height } = line.bounds;
          return (
            <svg
              key={line.key}
              viewBox="0 0 1 1"
              preserveAspectRatio="none"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 2,
                clipPath: `inset(${y * 100}% ${(1 - x - width) * 100}% ${(1 - y - height) * 100}% ${x * 100}%)`,
              }}
            >
              <path
                d={line.points.map((point, index) =>
                  `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
                ).join(' ')}
                fill="none"
                stroke="transparent"
                strokeWidth={20}
                vectorEffect="non-scaling-stroke"
                strokeLinecap="round"
                strokeLinejoin="round"
                role="button"
                tabIndex={0}
                aria-label={line.title}
                onClick={() => setActive(line)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setActive(line);
                  }
                }}
                style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
              >
                <title>{line.title}</title>
              </path>
            </svg>
          );
        })}
        {points.map(point => {
          const size = point.key === 'dog' ? 56 : 48;
          return (
            <button
              key={point.key}
              type="button"
              aria-label={point.title}
              title={point.title}
              onClick={() => setActive(point)}
              style={{
                position: 'absolute',
                left: `${point.x * 100}%`,
                top: `${point.y * 100}%`,
                transform: 'translate(-50%, -50%)',
                width: size,
                height: size,
                border: 0,
                borderRadius: '50%',
                background: 'transparent',
                cursor: 'pointer',
                padding: 0,
                zIndex: 3,
              }}
            />
          );
        })}
        {legends.map(legend => (
          <button
            key={legend.key}
            type="button"
            aria-label={legend.title}
            title={legend.title}
            onClick={() => setActive(legend)}
            style={{
              position: 'absolute',
              left: `${legend.bounds.x * 100}%`,
              top: `${legend.bounds.y * 100}%`,
              width: `${legend.bounds.width * 100}%`,
              height: `${legend.bounds.height * 100}%`,
              border: 0,
              background: 'transparent',
              cursor: 'pointer',
              padding: 0,
              minWidth: 0,
              minHeight: 0,
              boxSizing: 'border-box',
              zIndex: 4,
            }}
          />
        ))}
      </div>
      <Modal
        visible={Boolean(active)}
        title={active?.title}
        footer={null}
        onCancel={() => setActive(null)}
        destroyOnClose
      >
        <div style={{ whiteSpace: 'pre-line' }}>
          {active?.details}
        </div>
        {active?.code && (
          <div style={{ marginTop: 12 }}>
            銘柄コード：
            {link ? (
              <a href={link} target="_blank" rel="noopener noreferrer">
                {active.code}
              </a>
            ) : active.code}
          </div>
        )}
      </Modal>
    </>
  );
};

export default FrontierChart;
